import styled from 'styled-components'

export const PanelHeader = styled.div`
  background: ${({ theme }) => theme.colors.background.primary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};
  padding: 20px 12px 12px 12px;
  position: relative;
`

export const HeaderTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`

export const HeaderContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  flex: 1;
`

export const RadarChartContainer = styled.div`
  width: 80px;
  height: 80px;
  flex-shrink: 0;
`

export const PropertyInfoWrapper = styled.div`
  flex: 1;
  min-width: 0;
`

export const PropertyId = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 4px;
`

export const PropertyAddress = styled.h2`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 16px;
  line-height: 1.3;
  margin: 0;
`

export const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  background: ${({ theme }) => theme.colors.background.primary};
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  color: ${({ theme }) => theme.colors.text.secondary};

  &:hover {
    background: ${({ theme }) => theme.colors.background.secondary};
    transform: translateY(-1px);
  }
`

export const PropertyTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`

export const PropertyTag = styled.span<{
  $variant: 'green' | 'blue' | 'purple'
}>`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
  transition: all 0.2s;

  ${({ $variant }) => {
    switch ($variant) {
      case 'green':
        return `
          background: rgba(0, 179, 65, 0.1);
          color: #00994d;
        `
      case 'blue':
        return `
          background: rgba(0, 173, 230, 0.1);
          color: #0099cc;
        `
      case 'purple':
        return `
          background: rgba(128, 90, 213, 0.1);
          color: #7b61ff;
        `
      default:
        return `
          background: rgba(142, 142, 147, 0.1);
          color: #8e8e93;
        `
    }
  }}
`

export const ScrollableContent = styled.div<{ $isEmbedded?: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: ${({ $isEmbedded }) => ($isEmbedded ? 'visible' : 'auto')};
  padding-bottom: ${({ $isEmbedded }) =>
    $isEmbedded
      ? '24px'
      : '78px'}; /* Account for CTAContainer height if not embedded */
`

export const QuickStats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  padding: 20px 24px;
  background: ${({ theme }) => theme.colors.background.secondary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};
`

export const StatItem = styled.div`
  text-align: center;
`

export const StatValue = styled.span`
  font-size: 28px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  display: block;
  line-height: 1;
`

export const StatLabel = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: 4px;
`

export const QuickActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  background: ${({ theme }) => theme.colors.background.primary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};
`

export const ActionButton = styled.button<{
  $active?: boolean
  $variant?: 'favorite'
}>`
  flex: 1;
  min-width: 0;
  height: 48px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
  position: relative;
  color: ${({ theme }) => theme.colors.text.secondary};

  &:hover {
    background: ${({ theme }) => theme.colors.background.secondary};
    border-color: #999999;
    color: ${({ theme }) => theme.colors.text.primary};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  ${({ $active, $variant, theme }) =>
    $active && $variant === 'favorite'
      ? `
        background: #fff9e6;
        border-color: #ffd700;
        color: #f59e0b;
      `
      : $active
        ? `
        background: ${theme?.colors?.primaryLight || '#e6f7fc'};
        border-color: ${theme?.colors?.primary || '#00ade6'};
        color: ${theme?.colors?.primary || '#00ade6'};
      `
        : ''}

  ${({ $variant, $active }) =>
    $variant === 'favorite' &&
    !$active &&
    `
    &:hover:not(:disabled) {
      background: #fff9e6;
      border-color: #ffd700;
      color: #f59e0b;
    }
  `}
`

export const ActionButtonLabel = styled.span`
  font-size: 10px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: inherit;
  text-align: center;
  line-height: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  padding: 0 2px;
`

export const ActionDivider = styled.div`
  width: 1px;
  height: 32px;
  background: ${({ theme }) => theme.colors.border.default};
  margin: 0 ${({ theme }) => theme.spacing.xsmall};
  flex-shrink: 0;
`

export const Badge = styled.span`
  position: absolute;
  top: -6px;
  right: -6px;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 5px;
  border-radius: 10px;
  min-width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
`

export const TabBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 2px;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 10px;
  min-width: 18px;
  height: 16px;
  vertical-align: super;
  position: relative;
  top: -2px;
`

export const Tabs = styled.div`
  display: flex;
  background: ${({ theme }) => theme.colors.background.secondary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};
  position: sticky;
  top: 0;
  z-index: 1;
`

export const Tab = styled.button<{ $active?: boolean }>`
  padding: 12px 16px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.secondary};
  position: relative;
  transition: all 0.2s;
  font-family: inherit;

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }

  ${({ $active, theme }) =>
    $active &&
    `
    color: ${theme.colors.primary};

    &::after {
      content: '';
      position: absolute;
      bottom: -1px;
      left: 0;
      right: 0;
      height: 2px;
      background: ${theme.colors.primary};
    }
  `}
`

export const ContentArea = styled.div<{ $fullHeight?: boolean }>`
  flex: 1;
  /* padding: ${({ $fullHeight }) => ($fullHeight ? '0' : '24px')}; */
  ${({ $fullHeight }) =>
    $fullHeight &&
    `
    display: flex;
    flex-direction: column;
  `}
`

export const CTAContainer = styled.div`
  padding: 20px 24px;
  background: ${({ theme }) => theme.colors.background.primary};
  border-top: 1px solid ${({ theme }) => theme.colors.border.default};
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  position: absolute;
  bottom: 0px;
  width: 100%;

  @media (max-width: 768px) {
    bottom: 64px;
  }
`

export const CTAButton = styled.button<{ $variant: 'primary' | 'secondary' }>`
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;
  font-family: inherit;

  ${({ $variant, theme }) =>
    $variant === 'primary'
      ? `
        background: ${theme.colors.primary};
        color: white;
        border: none;

        &:hover {
          background: #00a1d6;
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(0, 173, 230, 0.3);
        }
      `
      : `
        background: ${theme.colors.background.primary};
        color: ${theme.colors.text.primary};
        border: 1px solid ${theme.colors.border.default};

        &:hover {
          background: ${theme.colors.background.secondary};
          border-color: #999999;
        }
      `}
`

export const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: ${({ theme }) => theme.colors.text.secondary};
`

export const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: ${({ theme }) => theme.colors.danger};
  text-align: center;
`
